"use client";
import { useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { format } from 'date-fns';
import { 
  Breadcrumb, 
  BreadcrumbList, 
  BreadcrumbItem, 
  BreadcrumbLink, 
  BreadcrumbPage, 
  BreadcrumbSeparator 
} from '@/components/ui/breadcrumb';
import { BreadcrumbSchema } from '@/components/seo/SchemaMarkup';
import { trackEngagement, pageview } from '@/lib/analytics';
import { BlogPost } from '@/app/SharedBlogData';
import TableOfContents from '@/components/blog/TableOfContents';
import ReadingProgress from '@/components/blog/ReadingProgress';
import siteConfig from '@/config/siteConfig';

interface EnhancedBlogWrapperProps {
  post: BlogPost;
  children: React.ReactNode;
  className?: string;
}

export default function EnhancedBlogWrapper({ 
  post, 
  children, 
  className = '' 
}: EnhancedBlogWrapperProps) {
  
  const [isSharing, setIsSharing] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  
  // Breadcrumb items using siteConfig
  const breadcrumbItems = useMemo(() => [
    { name: 'Home', url: siteConfig.baseUrl },
    { name: 'Blog', url: `${siteConfig.baseUrl}/blog` },
    { name: 'All Posts', url: `${siteConfig.baseUrl}/blog/all-posts` },
    { name: post.title, url: `${siteConfig.baseUrl}${post.customPath}` }
  ], [post.title, post.customPath]);

  const categoryDisplayName: { [key: string]: string } = useMemo(
    () => ({
      ai: "AI & Technology",
      analytics: "Analytics & Insights",
      commerce: "E-commerce & Marketplaces",
      "customer-experience": "Customer Experience & Loyalty",
      entrepreneurship: "Entrepreneurship",
      growth: "Growth & Case Studies",
      policy: "Global Policy",
    }),
    []
  );

  // Use date-fns for consistent date formatting between server and client
  const formattedDate = useMemo(() => {
    return format(new Date(post.date), 'MMMM d, yyyy');
  }, [post.date]);

  // Use the current URL for sharing
  const fullUrl = useMemo(() => {
    if (typeof window !== 'undefined') {
      return window.location.href;
    }
    const baseUrl = siteConfig.baseUrl.endsWith('/') ? siteConfig.baseUrl.slice(0, -1) : siteConfig.baseUrl;
    const path = post.customPath.startsWith('/') ? post.customPath : `/${post.customPath}`;
    return `${baseUrl}${path}`;
  }, [post.customPath]);

  useEffect(() => {
    // Track page view
    pageview(fullUrl);
    
    // Track blog post engagement
    trackEngagement('blog_post_view', 1);
    
    // Enhanced analytics for blog posts
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'blog_post_view', {
        event_category: 'Content',
        event_label: post.title,
        custom_parameter_post_id: post.id,
        custom_parameter_category: post.category,
        custom_parameter_author: post.author.name
      });
    }
  }, [post, fullUrl]);

  const handleSocialShare = (platform: string) => {
    trackEngagement('social_share', 1);
    
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'social_share', {
        event_category: 'Social',
        event_label: platform,
        custom_parameter_post_title: post.title,
        custom_parameter_post_id: post.id
      });
    }
  };

  const handleCopyLink = async () => {
    if (isSharing) return; // Prevent multiple simultaneous operations
    
    try {
      setIsSharing(true);
      
      // Always try clipboard first as it's more reliable than navigator.share
      if (typeof window !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(fullUrl);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
        
        trackEngagement('link_copy', 1);
        
        if (window.gtag) {
          window.gtag('event', 'link_copy', {
            event_category: 'Social',
            event_label: 'copy_link',
            custom_parameter_post_title: post.title
          });
        }
      }
    } catch (err) {
      console.error('Failed to copy link:', err);
      // Fallback: try to select text for manual copy
      try {
        const textArea = document.createElement('textarea');
        textArea.value = fullUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy also failed:', fallbackErr);
      }
    } finally {
      setIsSharing(false);
    }
  };

  const handleTwitterShare = () => {
    handleSocialShare('twitter');
    if (typeof window !== 'undefined') {
      const text = `${post.title}`;
      const via = siteConfig.organization.social.xHandle.replace('@', '');
      
      const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(fullUrl)}&via=${encodeURIComponent(via)}`;
      
      window.open(
        twitterUrl,
        '_blank',
        'width=550,height=420,scrollbars=yes,resizable=yes'
      );
    }
  };

  const handleLinkedInShare = () => {
    handleSocialShare('linkedin');
    if (typeof window !== 'undefined') {
      const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(fullUrl)}`;
      
      // Open in a new window with specific dimensions
      const popup = window.open(
        linkedInUrl,
        'linkedin-share',
        'width=550,height=420,scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no'
      );
      
      // Focus the popup window
      if (popup) {
        popup.focus();
      }
    }
  };

  const handleFacebookShare = () => {
    handleSocialShare('facebook');
    if (typeof window !== 'undefined') {
      const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fullUrl)}`;
      
      // Open in a new window with specific dimensions
      const popup = window.open(
        facebookUrl,
        'facebook-share',
        'width=550,height=420,scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no'
      );
      
      // Focus the popup window
      if (popup) {
        popup.focus();
      }
    }
  };

  return (
    <div className={`enhanced-blog-wrapper ${className}`}>
      {/* Reading Progress Bar - Must be first for proper z-index */}
      <ReadingProgress />
      
      {/* Breadcrumb Schema */}
      <BreadcrumbSchema items={breadcrumbItems} />
      
      {/* Blog Post Hero Section */}
      <div className="gradient-bg text-white">
        <div className="container py-16">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center mb-6">
              <span className="bg-white/20 text-white text-sm px-3 py-1 rounded-full">
                {categoryDisplayName[post.category] || post.category}
              </span>
              <span className="text-gray-200 text-sm ml-4">{formattedDate}</span>
              {post.readTime && (
                <>
                  <span className="text-gray-300 mx-2">•</span>
                  <span className="text-gray-200 text-sm">{post.readTime}</span>
                </>
              )}
            </div>
            
            <h1 className="text-white mb-6">{post.title}</h1>
            
            {post.excerpt && (
              <p className="text-xl text-gray-200 mb-8">{post.excerpt}</p>
            )}
            
            <div className="flex items-center justify-between">
              <Link 
                href={`/team/${post.author.id}`}
                className="flex items-center hover:opacity-80 transition-opacity"
              >
                <Image
                  src={post.author.image}
                  alt={post.author.name}
                  width={48}
                  height={48}
                  className="w-12 h-12 object-cover rounded-full mr-4"
                />
                <div>
                  <p className="text-white font-medium hover:text-[#00A4BD] transition-colors">
                    {post.author.name}
                  </p>
                  <p className="text-gray-200 text-sm">{post.author.jobTitle}</p>
                </div>
              </Link>
              
              {/* Social Sharing in Hero */}
              <div className="flex space-x-4">
                <button
                  onClick={handleTwitterShare}
                  className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white hover:text-gray-900 transition-colors"
                  aria-label="Share on X (Twitter)"
                  title="Share on X (Twitter)"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.205 2.25h3.308l-7.227 8.26 8.502 11.24h-6.657l-5.214-6.817-5.97 6.817H1.64l7.73-8.835L1.215 2.25H8.04l4.713 6.231 5.452-6.231Zm-1.161 17.52h1.833L7.045 4.126H5.078L16.044 19.77Z" />
                  </svg>
                </button>
                
                <button
                  onClick={handleLinkedInShare}
                  className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white hover:text-gray-900 transition-colors"
                  aria-label="Share on LinkedIn"
                  title="Share on LinkedIn"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                  </svg>
                </button>

                <button
                  onClick={handleFacebookShare}
                  className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white hover:text-gray-900 transition-colors"
                  aria-label="Share on Facebook"
                  title="Share on Facebook"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </button>
                
                <button
                  onClick={handleCopyLink}
                  disabled={isSharing}
                  className={`w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white hover:text-gray-900 transition-colors disabled:opacity-50 ${copySuccess ? 'bg-green-500 text-white' : ''}`}
                  aria-label={copySuccess ? 'Link copied!' : 'Copy link'}
                  title={copySuccess ? 'Link copied!' : 'Copy link'}
                >
                  {copySuccess ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Breadcrumb Navigation - Now below hero */}
      <div className="bg-gray-50 py-4">
        <div className="container">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link href="/">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link href="/blog">Blog</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link href="/blog/all-posts">All Posts</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{post.title}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>

      {/* Combined ToC and Content Section - Reduced spacing */}
      <div className="bg-white py-8">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            {/* Table of Contents - Reduced bottom margin */}
            <TableOfContents className="mb-4" />
            
            {/* Blog Content - Starts closer to ToC */}
            <article className="prose prose-lg max-w-none">
              {children}
            </article>

            {/* Share Section - Target for 100% reading progress */}
            <div className="mt-12 pt-8 border-t border-gray-200" data-share-section>
              <h3 className="text-xl mb-4">Share this article</h3>
              <div className="flex space-x-4">
                <button
                  onClick={handleTwitterShare}
                  className="w-10 h-10 bg-[#E6F7F9] rounded-full flex items-center justify-center hover:bg-[#00A4BD] hover:text-white transition-colors"
                  aria-label="Share on X (Twitter)"
                  title="Share on X (Twitter)"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#00A4BD]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.205 2.25h3.308l-7.227 8.26 8.502 11.24h-6.657l-5.214-6.817-5.97 6.817H1.64l7.73-8.835L1.215 2.25H8.04l4.713 6.231 5.452-6.231Zm-1.161 17.52h1.833L7.045 4.126H5.078L16.044 19.77Z" />
                  </svg>
                </button>
                
                <button
                  onClick={handleLinkedInShare}
                  className="w-10 h-10 bg-[#E6F7F9] rounded-full flex items-center justify-center hover:bg-[#00A4BD] hover:text-white transition-colors"
                  aria-label="Share on LinkedIn"
                  title="Share on LinkedIn"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#00A4BD]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                  </svg>
                </button>

                <button
                  onClick={handleFacebookShare}
                  className="w-10 h-10 bg-[#E6F7F9] rounded-full flex items-center justify-center hover:bg-[#00A4BD] hover:text-white transition-colors"
                  aria-label="Share on Facebook"
                  title="Share on Facebook"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#00A4BD]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </button>
                
                <button
                  onClick={handleCopyLink}
                  disabled={isSharing}
                  className={`w-10 h-10 bg-[#E6F7F9] rounded-full flex items-center justify-center hover:bg-[#00A4BD] hover:text-white transition-colors disabled:opacity-50 ${copySuccess ? 'bg-green-500 text-white' : ''}`}
                  aria-label={copySuccess ? 'Link copied!' : 'Copy link'}
                  title={copySuccess ? 'Link copied!' : 'Copy link'}
                >
                  {copySuccess ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#00A4BD]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}